import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Box } from '@mantine/core';
import { IconEdit, IconEye, IconPlus } from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import agentMutations from '@/api/mutitations/agent';
import { useDisclosure } from '@mantine/hooks';
import docsMutation from '@/api/mutitations/docs';
import { notifications } from '@mantine/notifications';
import { ACCORD_TYPE } from '@/helpers/enums';
import Table from '../Table';
import { extractFilterOptions, initialColumnFilters } from '../Table/Table.functions';
import AlternativeFundViewForm from './AlternativeFundViewForm';
import AlternativeFundEditForm from './AlternativeFundEditForm';
import AgentAlternativeFundCreate from '../AgentAlternativeFundCreate';
import Modal from '../Modal/Index';

const ViewAgentAlternativeFunds = (props) => {
  const {
    agent,
    id,
    setKey,
  } = props;

  const t = prepareTranslate();
  const exportOptions = ['excel'];

  const { mutateAsync: updateAgentItem } = agentMutations.useUpdateAgentItem();
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [viewOpened, { open: viewOpen, close: viewClose }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [responseOpened, { open: responseOpen, close: responseClose }] = useDisclosure(false);

  const [selectedAlternativeFund, setSelectedAlternativeFund] = useState(null);
  const [responseLabel, setResponseLabel] = useState('');
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(false);

  const handleViewClick = (alternativeFundId) => {
    const alternativeFund = agent.alternativeFunds?.find((a) => a._id === alternativeFundId);
    setSelectedAlternativeFund(alternativeFund);
    viewOpen();
  };

  const handleEditClick = (alternativeFundId) => {
    setSelectedAlternativeFund(alternativeFundId);
    openEdit();
  };

  const mockColumns = [
    {
      accessor: 'companyId.hebrew',
      title: t('components.viewAgentAlternativeFunds.company'),
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'branchId.hebrew',
      title: t('components.viewAgentAlternativeFunds.branch'),
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'alternativeFundNumber',
      title: t('components.viewAgentAlternativeFunds.alternativeFundNumber'),
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'uid',
      title: t('components.viewAgentAlternativeFunds.uid'),
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'accordType',
      title: t('components.viewAgentAlternativeFunds.accordType'),
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'displayCommission',
      title: 'עמלה %',
      sortable: true,
      isVisible: true,
      isFilterActive: true,
    },
    {
      accessor: 'actions',
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          action: (alternativeFundId) => handleViewClick(alternativeFundId),
          icon: <IconEye size={16} />,
          showLabel: true,
          isPrimary: true,
        },
        {
          title: 'עריכה',
          action: (alternativeFundId) => handleEditClick(alternativeFundId),
          icon: <IconEdit size={16} />,
          showLabel: true,
          isPrimary: true,
        },
      ],
    },
  ];

  const initialFilters = initialColumnFilters(mockColumns);
  const [columnFilters, setColumnFilters] = useState(initialFilters);

  const getData = async (start, end, query = {}, sort = {}) => {
    const result = { data: [], count: 0 };

    try {
      let filteredData = (agent.alternativeFunds || [])
        .filter((item) => item.status !== 'archived')
        .map((item) => ({
          ...item,
          accordType: ACCORD_TYPE.find((type) => type.value === item.accordType)?.label,
          displayCommission: item.accordType === ACCORD_TYPE[0].value
            ? item.agentCommission
            : item.employeeAgentCommission,
        }));

      if (query && Object.keys(query).length > 0) {
        filteredData = filteredData.filter((item) => Object.entries(query).every(([key, value]) => {
          if (!value) return true;

          const keys = key.split('.');
          let fieldValue = item;
          keys.forEach((k) => {
            if (fieldValue !== undefined) {
              fieldValue = fieldValue?.[k];
            }
          });

          if (Array.isArray(fieldValue)) {
            fieldValue = fieldValue.map((f) => (typeof f === 'object' ? f.hebrew || '' : f)).join(', ');
          }

          const valueStr = value.toString().trim().toLowerCase();
          const fieldStr = fieldValue?.toString().trim().toLowerCase() || '';
          return fieldStr.includes(valueStr);
        }));
      }

      if (sort?.columnAccessor && sort?.direction) {
        filteredData.sort((a, b) => {
          const keys = sort.columnAccessor.split('.');
          let aValue = a;
          let bValue = b;

          keys.forEach((k) => {
            aValue = aValue?.[k];
            bValue = bValue?.[k];
          });

          if (typeof aValue === 'string') {
            return sort.direction === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          if (typeof aValue === 'number') {
            return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
          }
          return 0;
        });
      }

      const newFilterOptions = extractFilterOptions(filteredData, mockColumns);
      setFilterOptions(newFilterOptions);

      result.data = filteredData;
      result.count = filteredData.length;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching alternativeFunds:', error);
    }

    return result;
  };

  const createFile = async (option) => {
    try {
      const filterQuery = {};
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value) {
          filterQuery[key] = value;
        }
      });

      const { data: allFilteredData } = await getData(1, 10000, filterQuery);

      const convertedData = allFilteredData.map((row) => {
        const convertedRow = { ...row };
        Object.keys(convertedRow).forEach((key) => {
          if (typeof convertedRow[key] === 'number') {
            convertedRow[key] = String(convertedRow[key]);
          }
        });
        return convertedRow;
      });

      const requestColumns = mockColumns.filter((column) => column.title !== 'פעולות');

      const exportData = {
        fileType: option,
        service: 'agent',
        columns: requestColumns,
        titleArray: [''],
        data: convertedData || [],
        query: filterQuery,
      };

      const fileTitleBase = `קרנות אלטרנטיביות של הסוכן ${agent.personalDetails.fullName}`;
      const fileExtension = option === 'pdf' ? '.pdf' : '.xlsx';
      const fileTitle = `${fileTitleBase}${fileExtension}`;
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.agencyCommissions.notification.errors.export.title'),
        message: t('pages.agencyCommissions.notification.errors.export.message'),
      });
      // eslint-disable-next-line no-console
      console.error('Error creating export file:', error);
      return null;
    }
  };

  const handleSaveAlternativeFund = async (updatedAlternativeFund) => {
    setLoading(true);
    try {
      await updateAgentItem({
        agentId: id,
        itemId: selectedAlternativeFund,
        key: 'alternativeFunds',
        data: updatedAlternativeFund,
      });

      closeEdit();
      setResponseLabel(t('components.editAlternativeFunds.updataSuccess'));
      responseOpen();
      setKey((prev) => prev + 1);
    } catch (err) {
      setResponseLabel(t('components.editAlternativeFunds.errorUpdating'));
      responseOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlternativeFund = async (deletionReason) => {
    setLoading(true);
    try {
      const alternativeFund = agent.alternativeFunds?.find(
        (a) => a._id === selectedAlternativeFund,
      );
      if (!alternativeFund) return;

      const updatedAlternativeFund = {
        ...alternativeFund,
        status: 'archived',
        deletionReason,
        archiveDate: new Date(),
      };

      await updateAgentItem({
        agentId: id,
        itemId: selectedAlternativeFund,
        key: 'alternativeFunds',
        data: updatedAlternativeFund,
      });

      closeEdit();
      setResponseLabel(t('components.editAlternativeFunds.deleteSuccess'));
      responseOpen();
      setKey((prev) => prev + 1);
    } catch (err) {
      setResponseLabel(t('components.editAlternativeFunds.errorDeleting'));
      responseOpen();
    } finally {
      setLoading(false);
    }
  };

  const responseActions = [
        <Button key='close' onClick={responseClose}>
            {t('global.close')}
        </Button>,
  ];

  return (
        <>
            <Box mb='md'>
                <Button
                    mb='md'
                    size='xs'
                    leftSection={<IconPlus />}
                    onClick={openCreate}
                >
                    {t('components.viewAgentAlternativeFunds.addAlternativeFund')}
                </Button>

            </Box>

            {agent && (
                <Table
                    columns={mockColumns}
                    getData={getData}
                    isColumnsActive={false}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    filterOptions={filterOptions}
                    key={agent.alternativeFunds}
                    exportOptions={exportOptions}
                    createFile={createFile}
                />
            )}

            <AgentAlternativeFundCreate
                open={openCreate}
                opened={createOpened}
                close={closeCreate}
                id={id}
                setKey={setKey}
            />

            <AlternativeFundViewForm
                opened={viewOpened}
                close={viewClose}
                alternativeFund={selectedAlternativeFund}
            />

          {
            selectedAlternativeFund && editOpened && <AlternativeFundEditForm
            opened={editOpened}
            close={closeEdit}
            alternativeFund={agent.alternativeFunds?.find((a) => a._id === selectedAlternativeFund)}
            onCancel={closeEdit}
            onSave={handleSaveAlternativeFund}
            onDelete={handleDeleteAlternativeFund}
            isLoading={loading}
            key={selectedAlternativeFund?._id}
          />
          }

            <Modal
                opened={responseOpened}
                onClose={responseClose}
                actions={responseActions}
                label={responseLabel}
            />
        </>
  );
};

ViewAgentAlternativeFunds.propTypes = {
  agent: PropTypes.object,
  id: PropTypes.string,
  setKey: PropTypes.func,
};

export default ViewAgentAlternativeFunds;
